import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Rocket, Info, ArrowLeftRight } from 'lucide-react';
import { WhatsAppEmbeddedSignup } from '@/components/WhatsAppEmbeddedSignup';
import { toast } from '@/hooks/use-toast';
import metaIcon from '/onboardingimg/meta.svg';

/**
 * ConnectMetaModal — the "Connect once, activate all" onboarding modal (design
 * ported pixel-for-pixel from wenext-onboarding). Unlike the design mock, the
 * "Continue with Meta" button drives the REAL Meta embedded-signup flow via
 * <WhatsAppEmbeddedSignup> — the same battle-tested path used by
 * ConnectBusinessAccountDialog. Three phases:
 *   intro      — the pretty consent screen + "Continue with Meta".
 *   connecting — the real embedded signup (auto-launches Meta's secure popup;
 *                may show a phone-number picker if the WABA has several).
 *   success    — "Meta connected" confirmation + "Start automation".
 */
interface ConnectMetaModalProps {
  open: boolean;
  onClose: () => void;
  /** Fired once when a WhatsApp Business account is successfully connected. */
  onConnected?: (accounts: any[]) => void;
}

type Step = 'intro' | 'connecting' | 'success';

export function ConnectMetaModal({ open, onClose, onConnected }: ConnectMetaModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('intro');
  const [connectedName, setConnectedName] = useState<string | null>(null);

  // Always start from the intro each time the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setStep('intro');
      setConnectedName(null);
    }
  }, [open]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Refresh the org's business-account list so the rest of the app (header
  // selector, banner, setup card) picks up the new connection.
  const refreshBusinessAccounts = () => {
    queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
  };

  const handleClose = () => {
    // Leaving the success state is when we finally refresh the account list.
    if (step === 'success') refreshBusinessAccounts();
    onClose();
  };

  // Escape closes — but not mid-connection, so an accidental key can't abandon
  // the Meta flow (the header X is always available as an explicit exit).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'connecting') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  const handleEmbeddedSuccess = (accounts: any[]) => {
    setConnectedName(accounts?.[0]?.name ?? null);
    setStep('success');
    const count = accounts?.length ?? 1;
    toast({
      title: 'WhatsApp Connected',
      description: `Successfully connected ${count} WhatsApp Business account${count === 1 ? '' : 's'}.`,
    });
    onConnected?.(accounts);
  };

  const handleEmbeddedError = (message: string) => {
    // The embedded component surfaces the error inline and lets the user retry;
    // keep the modal in the connecting phase so context isn't lost.
    toast({ title: 'Connection failed', description: message, variant: 'destructive' });
  };

  const handleStartAutomation = () => {
    refreshBusinessAccounts();
    onClose();
    navigate('/home/automation/flows');
  };

  if (!open) return null;

  // Portaled to <body> so the dim + blur overlay covers the ENTIRE viewport —
  // including the top bar and sidebar. Rendered inside Dashboard (<main>, a
  // z-0 stacking context) it was trapped below the header/sidebar; the portal
  // lifts it to the root context and z-[100] sits above all app chrome
  // (header z-50, subscription banner z-70).
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#101828]/40 backdrop-blur-sm"
      onClick={() => {
        if (step !== 'connecting') handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Connect your Meta Business account"
    >
      <div
        className="relative w-full max-w-[480px] max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button lives OUTSIDE the scroll area and header so no sibling
            (dot grid, icon tiles, title) can ever overlap it and swallow the
            click. Highest z-index in the modal. */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 z-30 w-8 h-8 rounded-full bg-white/80 hover:bg-white text-[#475467] backdrop-blur flex items-center justify-center shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-[0_20px_60px_rgba(16,24,40,0.2)]">
        {/* Header — dotted pastel field with a soft green→blue wash + WeNext ↔ Meta */}
        <div
          className="relative overflow-hidden px-6 pt-6 pb-5 text-[#101828]"
          style={{
            backgroundColor: '#F5F8FF',
            backgroundImage: 'linear-gradient(to bottom, #EAF1FF 0%, #EDF9F1 52%, #FFFFFF 100%)',
          }}
        >
          {/* Dot grid on its own layer, masked so it fades gradually into the
              white lower half instead of cutting off abruptly. */}
          <div
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              backgroundImage: 'radial-gradient(rgba(148,163,184,0.30) 1px, transparent 1px)',
              backgroundSize: '16px 16px',
              maskImage: 'linear-gradient(to bottom, black 0%, black calc(28% + 50px), transparent calc(78% + 50px))',
              WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(28% + 50px), transparent calc(78% + 50px))',
            }}
          />

          <div className="relative z-10 pt-4 pb-2">
            <div className="relative flex items-center justify-center gap-5">
              <IconTile color="#0EB64F" label="WeNext">
                <WeNextMark />
              </IconTile>

              {/* Gray swap/exchange icon between the two tiles. */}
              <ArrowLeftRight className="-mt-5 h-5 w-5 shrink-0 text-[#94A3B8]" strokeWidth={1.6} aria-hidden />

              <IconTile color="#0081FB" label="Meta">
                <img src={metaIcon} alt="" className="h-7 w-7 object-contain" />
              </IconTile>
            </div>
          </div>

          <div className="relative z-10 mt-5 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#475467]">Meta Business</p>
            <h2 className="mt-1 font-serif text-[22px] leading-[1.15] tracking-[-0.01em] text-[#101828]">
              {step === 'success' ? 'You’re all set!' : 'Connect once, activate all'}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-1 pb-6">
          {step === 'intro' && (
            <>
              <p className="text-[13.5px] leading-relaxed text-[#475467] text-center">
                We&apos;ll open Meta so you can authorize WeNext. One login unlocks WhatsApp, Instagram and Facebook.
              </p>
              <ul className="mt-6 space-y-2.5 text-[13px] text-[#344054]">
                <Row>Read and reply to messages on your behalf</Row>
                <Row>Access your Business pages and Instagram accounts</Row>
                <Row>Send WhatsApp Business templates you&apos;ve approved</Row>
              </ul>
              <p className="mt-8 mb-6 flex items-center justify-center gap-1.5 text-[12px] text-[#6B7280] text-center">
                <Info className="h-3.5 w-3.5 shrink-0" />
                You can disconnect any time from Account settings.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep('connecting')}
                  className="flex-1 rounded-xl py-2.5 text-[14px] font-semibold text-white flex items-center justify-center gap-2 transition hover:opacity-90"
                  style={{ backgroundColor: 'var(--secondary-color)' }}
                >
                  <img src={metaIcon} alt="" className="h-4 w-4 object-contain brightness-0 invert" />
                  Continue with Meta
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-xl border border-[#E4E7EC] bg-white hover:bg-[#F9FAFB] text-[#101828] px-4 py-2.5 text-[14px] font-medium"
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {step === 'connecting' && (
            <>
              <p className="mb-4 text-center text-[13px] leading-relaxed text-[#6B7280]">
                Complete the secure sign-in in the Meta window — keep this open and we&apos;ll confirm here once you&apos;re done.
              </p>
              <WhatsAppEmbeddedSignup autoLaunch onSuccess={handleEmbeddedSuccess} onError={handleEmbeddedError} />
            </>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#E7F7EC] flex items-center justify-center text-[#0EB64F]">
                <span className="w-7 h-7">
                  <CheckMark />
                </span>
              </div>
              <p className="mt-4 text-[15px] font-semibold text-[#101828]">Meta connected</p>
              <p className="mt-1 text-[13px] text-[#6B7280]">
                {connectedName ? `${connectedName} is now linked to WeNext.` : 'Your WhatsApp Business account is now linked to WeNext.'}
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleStartAutomation}
                  className="w-full rounded-xl bg-[#101828] hover:bg-[#0F172A] text-white py-2.5 text-[14px] font-semibold flex items-center justify-center gap-2"
                >
                  <Rocket className="h-4 w-4" />
                  Start automation
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-[13px] font-medium text-[#6B7280] hover:underline"
                >
                  Maybe later
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function IconTile({ color, label, children }: { color: string; label: string; children: ReactNode }) {
  return (
    <div className="relative">
      <div
        className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center ring-1 ring-black/5"
        style={{ boxShadow: `0 8px 24px ${color}22, 0 1px 2px rgba(16,24,40,0.06)` }}
      >
        {children}
      </div>
      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#475467] text-center">{label}</p>
    </div>
  );
}

/** WeNext arrow-in-box mark (green). Ported from the design source. */
function WeNextMark() {
  return (
    <svg width="28" height="28" viewBox="0 0 20 20" fill="none" aria-hidden>
      <rect x="1" y="1" width="18" height="18" rx="3.5" stroke="#0EB64F" strokeWidth="1.5" />
      <path
        d="M14.4 9.6 L10.8 13.2 C10.7 13.3 10.6 13.4 10.4 13.4 C10.2 13.4 10.1 13.3 10 13.2 C9.9 13.1 9.8 13 9.8 12.8 C9.8 12.6 9.9 12.5 10 12.4 L12.5 9.8 H5.2 C5.1 9.8 4.9 9.8 4.8 9.6 C4.7 9.5 4.6 9.4 4.6 9.2 C4.6 9.1 4.7 8.9 4.8 8.8 C4.9 8.7 5.1 8.6 5.2 8.6 H12.5 L10 6.1 C9.9 5.9 9.8 5.8 9.8 5.6 C9.8 5.5 9.9 5.3 10 5.2 C10.1 5.1 10.2 5 10.4 5 C10.6 5 10.7 5.1 10.8 5.2 L14.4 8.8 C14.5 8.9 14.5 8.9 14.5 9 C14.6 9.1 14.6 9.1 14.6 9.2 C14.6 9.3 14.5 9.4 14.5 9.4 C14.5 9.5 14.5 9.5 14.4 9.6 Z"
        fill="#0EB64F"
      />
    </svg>
  );
}

function CheckMark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-0.5 w-4 h-4 shrink-0 text-[#0EB64F]">
        <CheckMark />
      </span>
      {children}
    </li>
  );
}

export default ConnectMetaModal;
